import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { supportUrl } from '@/lib/links'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Container from '@/components/generals/container'
import PageHeader from '@/components/generals/page-header'
import SmartLink from '@/components/generals/smart-link'

interface PolicySectionProps {
    id: string
    title: string
    children: ReactNode
}

const summary = [
    "This website has no accounts, no forms and sets no cookies of its own.",
    "Visits are counted with privacy-friendly, cookieless statistics on the published site.",
    "The Ocuvia application is local-first. Your images and records stay on your computer.",
    "If you work with real patient data, you are responsible for consent and data protection."
]

const link = 'font-medium text-ink underline decoration-taupe underline-offset-[5px] transition-colors duration-300 hover:decoration-ink'

const PolicySection: FC<PolicySectionProps> = ({ id, title, children }): ReactNode => {
    return (
        <section id={id} aria-labelledby={`${id}-title`} className='border-t border-taupe py-10 lg:py-12'>
            <h2 id={`${id}-title`} className='font-heading text-[2rem] leading-tight text-ink sm:text-[2.25rem]'>
                {title}
            </h2>

            <div className='mt-5 space-y-4 text-[15.5px] leading-relaxed text-ink-soft'>
                {children}
            </div>
        </section>
    )
}

const PrivacyPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Privacy policy"
                description="How the Ocuvia website and the local-first Ocuvia application handle data."
            />

            <PageHeader
                eyebrow="Privacy policy"
                title="Privacy, in plain words."
                lead="How this website and the Ocuvia application handle data. Last updated October 31, 2026."
            />

            <div className='bg-cream'>
                <Container className='py-14 sm:py-20 lg:py-24'>
                    <div className='mx-auto max-w-3xl'>
                        <div className='rounded-[24px] border border-taupe bg-[#FDFCF9] p-7 sm:p-9'>
                            <h2 className='text-[12px] font-medium uppercase tracking-[0.18em] text-ink-soft'>
                                In short
                            </h2>

                            <ul className='mt-5 space-y-3.5'>
                                {summary.map(item => (
                                    <li key={item} className='flex gap-3 text-[15.5px] leading-relaxed text-ink'>
                                        <span aria-hidden='true' className='mt-2.5 size-1.5 shrink-0 rounded-full bg-terracotta' />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className='mt-12'>
                            <PolicySection id='website' title="This website">
                                <p>
                                    You can read this website and download Ocuvia without creating an account or sharing any personal details. The website has no contact forms, and it does not set cookies of its own.
                                </p>

                                <p>
                                    To understand how the site is used, the published site counts visits with privacy-friendly, cookieless statistics. These are aggregated figures, such as which pages are viewed, the referring website, and the country, browser, operating system and type of device. They are not used to identify you.
                                </p>

                                <p>
                                    Like any website, the servers that deliver these pages process technical information, such as your IP address, in order to send the pages to your browser securely.
                                </p>
                            </PolicySection>

                            <PolicySection id='downloads' title="Downloads">
                                <p>
                                    Ocuvia builds may be hosted by a third party, such as GitHub. When you download a file from such a service, it handles that request under its own privacy terms.
                                </p>
                            </PolicySection>

                            <PolicySection id='application' title="The Ocuvia application">
                                <p>
                                    Ocuvia is designed as a local-first application. Retinal images, patient-linked records and analysis results are stored on your computer in encrypted local storage, and the application does not send them to this website.
                                </p>

                                <p>
                                    Read the release notes of each build for anything specific to that version, for example if a feature needs an internet connection.
                                </p>
                            </PolicySection>

                            <PolicySection id='patient-data' title="Working with patient data">
                                <p>
                                    Ocuvia is a research prototype. If you use it with images or records of real people, you are responsible for obtaining appropriate consent and ethical approval, and for following the data protection laws that apply to you, such as the GDPR or HIPAA.
                                </p>

                                <p>
                                    Use de-identified images wherever you can, and never include identifiable patient images or data when you report a bug or ask for help.
                                </p>
                            </PolicySection>

                            <PolicySection id='changes' title="Changes to this policy">
                                <p>
                                    If this policy changes, the updated version will be published on this page with a new date at the top.
                                </p>
                            </PolicySection>

                            <PolicySection id='contact' title="Contact">
                                <p>
                                    For privacy questions, use the Ocuvia support channel:{' '}
                                    <SmartLink href={supportUrl} className={link} pendingClassName='text-ink'>
                                        Contact support
                                    </SmartLink>
                                </p>

                                <p>
                                    For how Ocuvia may be used, see the <Link href='/license' className={link}>license page</Link>.
                                </p>
                            </PolicySection>
                        </div>
                    </div>
                </Container>
            </div>
        </Layout>
    )
}

export default PrivacyPage
